import { useCallback } from 'react';
import { PointerSensor, TouchSensor, useSensor, useSensors, SensorDescriptor } from '@dnd-kit/core';
import dayjs from 'dayjs';
import { message } from 'antd';
import { appointmentApi } from '../../../services/appointmentApi';
import { useAuth } from '../../../contexts/AuthContext';
import { logger } from '../../../utils/logger';
import { resolveTechColor, TechLane } from '../useSchedulingData';
import { DropIntent } from '../views/WeekGrid';

/**
 * Extra context used only to make the drag OBSERVABLE — it does not change what
 * gets written. `intent` alone can't say what the appointment looked like before
 * the move: its `technicianId` is the TARGET lane's tech, and the drag payload's
 * origin tech is the tech of the lane the block was rendered in, which is
 * `undefined` for anything sitting in the catch-all Unassigned lane (unassigned
 * appts, but also appts on an inactive tech or an owner). Reading the prior
 * technician off the loaded appointment instead keeps the log honest.
 */
export interface ApptDragContext {
  appts?: Array<{ id?: number; technician_id?: number | null }>;
  techs?: TechLane[];
}

/**
 * Drag sensors + drop→API wiring shared by the scheduling grids.
 *
 * TouchSensor uses a 250ms long-press delay (8px tolerance) so a normal
 * swipe/scroll on mobile is not hijacked into a drag — the finger has to
 * hold still past the delay before the block picks up. PointerSensor
 * covers mouse/pen with a small movement threshold so desktop drags still
 * feel immediate.
 *
 * WP-01: a drag that SUCCEEDS must refresh, not just one that fails. Nothing
 * here updates local state optimistically, so without a refresh on the success
 * path the grids keep rendering the pre-move `technician_id` — which is the one
 * field both the lane bucketing and the block's technician colour derive from.
 * The result the owner saw: a committed reassignment (server 200 OK) left the
 * block sitting in the old technician's lane in the old technician's colour
 * until the page was reloaded by hand. The two edit modals already funnel
 * through the same `reload()`, so this makes drag consistent with them rather
 * than introducing a second refresh strategy. Refetching (rather than patching
 * local state from the response) also picks up anything the write moved that the
 * response doesn't carry — recomputed day segments, and a `techs` list whose
 * colours may have changed.
 */
export function useApptDrag(
  reload: () => void,
  context?: ApptDragContext,
): {
  sensors: SensorDescriptor<any>[];
  onDragEnd: (intent: DropIntent) => Promise<void>;
} {
  const { user } = useAuth();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } })
  );

  const onDragEnd = useCallback(
    async (intent: DropIntent) => {
      const changedByUserId = user?.id ?? 0;
      const techs = context?.techs ?? [];

      const priorTechnicianId =
        context?.appts?.find((a) => a.id === intent.appointmentId)?.technician_id ?? null;
      const technicianChanged = intent.technicianId !== undefined;
      const newTechnicianId = technicianChanged ? intent.technicianId! : priorTechnicianId;

      // Both colours are resolved against the techs list AS CURRENTLY LOADED,
      // i.e. at drag time — this runs before the refresh, so if a technician's
      // calendar_color changed since the calendar last loaded, the logged
      // `resolvedTechColor` is the colour the UI believed, not the one that will
      // paint after the refetch. That is the useful thing to record: it is
      // exactly the discrepancy you would be hunting. `undefined` (no colour
      // resolvable) is normalized to null so the key is never silently dropped —
      // "this tech has no colour" is what you need to see when a block goes grey.
      const priorTechColor = resolveTechColor(techs, priorTechnicianId) ?? null;
      const resolvedTechColor = resolveTechColor(techs, newTechnicianId) ?? null;

      const diag = {
        appointmentId: intent.appointmentId,
        priorTechnicianId,
        newTechnicianId,
        technicianChanged,
        priorTechColor,
        resolvedTechColor,
        date: intent.date,
        startMinutes: intent.startMinutes,
        startChanged: intent.startChanged,
      };

      try {
        // Decoupled on purpose: a pure tech-lane move (startChanged false)
        // must not also rewrite the appt's start time, and a pure time move
        // must not also touch its technician. Each write only fires when its
        // own dimension actually changed.
        if (intent.startChanged) {
          const newStart = dayjs(intent.date)
            .startOf('day')
            .add(intent.startMinutes, 'minute')
            .format('YYYY-MM-DD HH:mm:ss');
          await appointmentApi.reschedule(intent.appointmentId, {
            new_start_time: newStart,
            changed_by_user_id: changedByUserId,
          });
        }

        if (intent.technicianId !== undefined) {
          await appointmentApi.changeTechnician(intent.appointmentId, {
            new_technician_id: intent.technicianId,
            changed_by_user_id: changedByUserId,
          });
        }

        logger.info('[scheduling] appointment move committed', diag);
        // Re-read the schedule so the lane AND the technician colour reflect what
        // was just written. Without this the write lands but the calendar lies.
        reload();
      } catch (err) {
        logger.error('[scheduling] appointment move failed — reverting', {
          ...diag,
          reason: err instanceof Error ? err.message : String(err),
        });
        message.error('Failed to move the appointment — reverting.');
        reload();
      }
    },
    // Depend on the context's ARRAYS, not the wrapper object — callers build the
    // wrapper inline each render, which would otherwise churn onDragEnd's
    // identity on every render for no behavioural gain.
    [user, reload, context?.appts, context?.techs]
  );

  return { sensors, onDragEnd };
}
